import { cloneDeep } from 'lodash-es';

import { EnvironmentConfig } from '@api/environmentsApi/models/EnvironmentConfig';
import { ProvisioningStatus } from '@api/environmentsApi/models/ProvisioningStatus';
import { Environment, EnvironmentHierarchyLookup } from '@api/hierarchyApi/models/Hierarchy';
import { ProvisioningRequest } from '@api/provisioningApiV2/models/Provisioning';
import { AuthHelper } from '@shared/auth/AuthHelper';
import { FieldValidator } from '@shared/components/ValidatedField/ValidatedField';
import { t } from '@shared/i18n';
import { Dictionary } from '@shared/interfaces/Dictionary';

export const tagRegex = /^\w[\w ]{0,59}$/;

const SEARCHABLE_FIELDS = ['envid', 'name', 'tags', 'description', 'geography', 'status', 'externalIdentifier'];

export const isEnvMatch = (env: EnvironmentHierarchyLookup, filter: string) => {
	const filterValue = filter.trim().toLocaleLowerCase();

	return SEARCHABLE_FIELDS.some((field) => env[field]?.toLocaleLowerCase()?.includes(filterValue));
};

export const apiRequestValidator = (siblingApiIds: string[]): FieldValidator[] => {
	const externalIdentifierRegexStr = `^[\\w-]+$`;

	return [
		{ type: 'regex', regex: new RegExp(externalIdentifierRegexStr), errorMessage: t('General.NameValidation') },
		{ type: 'unique', values: siblingApiIds },
	];
};

export const checkIfActive = (env: EnvironmentConfig | EnvironmentHierarchyLookup) => env?.status === ProvisioningStatus.Active;

export const checkIfProvisioning = (env: EnvironmentConfig | EnvironmentHierarchyLookup) =>
	env.status === ProvisioningStatus.New || env.status === ProvisioningStatus.Creating;

export const checkIfProcessing = (env: EnvironmentConfig | EnvironmentHierarchyLookup) =>
	env.status === ProvisioningStatus.New || env.status === ProvisioningStatus.Creating || env.status === ProvisioningStatus.SoftDeleting;

export const checkIfFailed = (env: EnvironmentConfig | EnvironmentHierarchyLookup) =>
	env.status === ProvisioningStatus.CreateFailed || env.status === ProvisioningStatus.Error;

/**
 * Easier to mock as a method
 * Only distinguishes between PROD and everything else (INT, DEV, Local)
 * Use hasAtLeastOneSpartaApi() to detect local+dev sites only
 */
export const isProduction = () => {
	return window.siteConfig.environment === 'Production';
};

export const getEnvironmentIdFromTestHooks = () => {
	return window.testHooks.stores.APP.environment?.envid || window.testHooks.stores.APP.envIdToLoad;
};

export const getAssignedRolesFromTestHooks = () => {
	return window.testHooks.stores.APP.tenantConfig?.data?.assignedRoles || [];
};

export const getTenantId = () => {
	return AuthHelper.user?.tenantId;
};

export const getTenantName = () => {
	return AuthHelper.tenantName;
};

export const isGoodProvisioningRequest = (request: ProvisioningRequest, firstEnv?: boolean) => {
	const hasGeo = !!request?.geography;

	return firstEnv ? hasGeo : hasGeo && request?.friendlyName;
};

export const getParentsListInHierarchyOrder = (
	hierarchyLookup: Dictionary<EnvironmentHierarchyLookup>,
	envId: string,
	envsInHierarchyOrder: EnvironmentHierarchyLookup[],
): EnvironmentHierarchyLookup[] => {
	let curr = hierarchyLookup[envId];
	const clonedEnvsInHierarchyOrder = cloneDeep(envsInHierarchyOrder); // to keep function pure for render
	if (!curr) {
		return clonedEnvsInHierarchyOrder;
	}
	clonedEnvsInHierarchyOrder.push(curr);

	if (!curr.parentEnvironmentId) {
		return clonedEnvsInHierarchyOrder.reverse();
	} else {
		const parent = hierarchyLookup[curr.parentEnvironmentId];
		curr = parent;

		return getParentsListInHierarchyOrder(hierarchyLookup, curr?.envid, clonedEnvsInHierarchyOrder);
	}
};

export const getParentsInfo = (lookup: Dictionary<EnvironmentHierarchyLookup>, currentEnvId: string) => {
	const parentList = getParentsListInHierarchyOrder(lookup, currentEnvId, []);

	return { parentIds: parentList.map((env) => env.envid), path: parentList.map((env) => env.name).join('/') };
};

export const getEnvironmentStatusText = (status: ProvisioningStatus) => {
	const { New, Creating, Active, CreateFailed, SoftDeleting, Error, Deleted, Deleting, DeleteFailed } = ProvisioningStatus;
	let text: string;
	switch (status) {
		case New: {
			text = t('General.New');
			break;
		}
		case Creating: {
			text = t('General.Creating');
			break;
		}
		case Active: {
			text = t('General.Active');
			break;
		}
		case SoftDeleting: {
			text = t('General.Deleting');
			break;
		}
		case Error:
		case CreateFailed: {
			text = t('General.Failed');
			break;
		}
		case Deleted:
		case Deleting:
		case DeleteFailed:
		default:
			break;
	}

	return text;
};

export const isValid = (env: Environment) => {
	if (!env) {
		return false;
	}

	return env.hasAccess && env.status === ProvisioningStatus.Active;
};
